import React, { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Button,
  Close,
  Flex,
  H4,
  Input,
  InputModal,
  Line,
  SelectDropdown,
  Span,
} from '../../lib/components'
import { investorServices, manualEntryServices } from '../../services'
import { Role } from '../../lib/types'
import { showBanner } from '../../global-state/banner-slice'
import './manual-share-sale-modal.scss'

type ClientOption = { investor_id: number; name: string; email: string }

type ManualShareSaleModalProps = {
  opportunityId: string | number
  opportunityTitle?: string
  onClose: () => void
  onSuccess: () => void
}

const formatDollars = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

/** One key per modal session: a retry or second tab submit records a single sale. */
const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`

const CLIENT_SEARCH_LIMIT = 10

/** Exactly what will be recorded (today resolves to the current time). */
const formatSaleDate = (iso?: string) => (iso ? new Date(iso).toLocaleString() : 'Now')

const todayLocalDate = () => new Date().toLocaleDateString('en-CA')

/** Noon on the chosen day, or now when that would be in the future (e.g. today). */
const toSaleDateIso = (date: string): string | undefined => {
  if (!date) return undefined
  const chosen = new Date(`${date}T12:00:00`)
  const now = new Date()
  return (chosen.getTime() > now.getTime() ? now : chosen).toISOString()
}

function ManualShareSaleModal({
  opportunityId,
  opportunityTitle,
  onClose,
  onSuccess,
}: ManualShareSaleModalProps) {
  const dispatch = useDispatch()
  const idempotencyKey = useRef(newIdempotencyKey())
  const [keyword, setKeyword] = useState('')
  const [clients, setClients] = useState<ClientOption[]>([])
  const [investorId, setInvestorId] = useState('')
  const [shares, setShares] = useState('')
  const [pricePerShare, setPricePerShare] = useState('')
  const [saleDate, setSaleDate] = useState(todayLocalDate())
  const [note, setNote] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const sharesNum = Number(shares)
  const priceNum = Number(pricePerShare)
  const sharesValid = Number.isInteger(sharesNum) && sharesNum > 0
  const priceCents = Math.round(priceNum * 100)
  // Dollars with at most 2 decimals, so the amount is exact in cents (as on the server).
  const priceValid =
    Number.isFinite(priceNum) && priceCents > 0 && Math.abs(priceNum * 100 - priceCents) < 1e-6
  const totalCents = sharesValid && priceValid ? sharesNum * priceCents : 0
  const selectedClient = clients.find((c) => String(c.investor_id) === investorId)
  const canReview = !!selectedClient && sharesValid && priceValid && totalCents > 0

  const searchClients = async () => {
    setError('')
    const response: any = await investorServices.getInvestorsList(Role.investor, {
      keyword,
      limit: String(CLIENT_SEARCH_LIMIT),
      offset: '0',
    })
    if (response && response.status === 200) {
      setClients(response.data ?? [])
      setInvestorId('')
    } else {
      setError('Could not search clients')
    }
  }

  const submit = async () => {
    if (!selectedClient) return
    setBusy(true)
    setError('')
    const response = await manualEntryServices.createManualShareSale(opportunityId, {
      investorId: selectedClient.investor_id,
      shares: sharesNum,
      pricePerShare: priceNum,
      saleDate: toSaleDateIso(saleDate),
      idempotencyKey: idempotencyKey.current,
      note: note.trim() || undefined,
    })
    setBusy(false)
    if (response?.status === 200) {
      dispatch(
        showBanner({
          text: `Recorded sale of ${sharesNum} shares for ${
            selectedClient.name
          }. Wallet credited ${formatDollars(response.data.totalAmountCents)}.`,
          variant: 'success-banner',
        }),
      )
      onSuccess()
      onClose()
      return
    }
    setConfirming(false)
    setError(response?.data?.message || 'Could not record the manual sale')
  }

  return (
    <InputModal onBackdropClick={busy ? undefined : onClose}>
      <div
        className='input-modal input-modal--scrollable manual-sale'
        data-testid='manual-share-sale-modal'
      >
        <Flex variant='space-between'>
          <H4 text='Manual share sale' />
          <Close height='24px' classes={['pointer']} onClick={busy ? undefined : onClose} />
        </Flex>
        {opportunityTitle && <Span variant='thin' text={opportunityTitle} />}
        <Line />

        {confirming && selectedClient ? (
          <Flex variant='column' classes={['gap-12']}>
            <Span variant='bold' text='Confirm the details below' />
            <Flex variant='space-between'>
              <Span variant='bold' text='Client' />
              <Span variant='thin' text={`${selectedClient.name} (${selectedClient.email})`} />
            </Flex>
            <Flex variant='space-between'>
              <Span variant='bold' text='Shares sold' />
              <Span variant='thin' text={String(sharesNum)} />
            </Flex>
            <Flex variant='space-between'>
              <Span variant='bold' text='Price per share' />
              <Span variant='thin' text={formatDollars(priceCents)} />
            </Flex>
            <Flex variant='space-between'>
              <Span variant='bold' text='Wallet credit' />
              <Span variant='thin' text={formatDollars(totalCents)} />
            </Flex>
            <Flex variant='space-between'>
              <Span variant='bold' text='Sale date' />
              <Span variant='thin' text={formatSaleDate(toSaleDateIso(saleDate))} />
            </Flex>
            <Span
              variant='thin'
              text='Units are deducted from the client and the total is credited to their wallet, with a transaction and wallet ledger entry.'
            />
            {error && <Span variant='thin' classes={['manual-sale__error']} text={error} />}
            <Flex variant='row' classes={['gap-12']}>
              <Button
                text='Confirm sale'
                variant='primary-btn'
                isDisabled={busy}
                handleClick={() => void submit()}
                dataTestId='manual-sale-confirm'
              />
              <Button
                text='Back'
                variant='secondary-btn'
                isDisabled={busy}
                handleClick={() => setConfirming(false)}
              />
            </Flex>
          </Flex>
        ) : (
          <Flex variant='column' classes={['gap-12']}>
            <Input
              type='text'
              name='clientKeyword'
              labelText='Find client'
              placeholder='Name or email'
              value={keyword}
              handleChange={(e) => setKeyword(e.target.value)}
            />
            <Button
              text='Search clients'
              variant='secondary-btn'
              handleClick={() => void searchClients()}
              dataTestId='manual-sale-search'
            />
            {clients.length >= CLIENT_SEARCH_LIMIT && (
              <Span
                variant='thin'
                text={`Showing the first ${CLIENT_SEARCH_LIMIT} matches. Refine the search to find the right client.`}
              />
            )}
            {clients.length > 0 && (
              <SelectDropdown
                name='investorId'
                id='manualSaleClient'
                formId=''
                list={clients.map((c) => ({
                  text: `${c.name} (${c.email})`,
                  value: String(c.investor_id),
                }))}
                value={investorId}
                placeholder='Select client'
                handleChange={(val) => setInvestorId(val)}
                dataTestId='manual-sale-client-select'
              />
            )}
            <Input
              type='number'
              name='shares'
              labelText='Number of shares'
              placeholder='Whole number'
              value={shares}
              handleChange={(e) => setShares(e.target.value)}
            />
            <Input
              type='number'
              name='pricePerShare'
              labelText='Price per share ($)'
              placeholder='Price per share in USD'
              value={pricePerShare}
              handleChange={(e) => setPricePerShare(e.target.value)}
            />
            <Input
              type='date'
              name='saleDate'
              labelText='Sale date'
              value={saleDate}
              handleChange={(e) => setSaleDate(e.target.value)}
            />
            <Input
              type='text'
              name='note'
              labelText='Note (optional)'
              placeholder='Reference note'
              value={note}
              handleChange={(e) => setNote(e.target.value)}
            />
            <Span variant='bold' text={`Total credited to wallet: ${formatDollars(totalCents)}`} />
            {error && <Span variant='thin' classes={['manual-sale__error']} text={error} />}
            <Flex variant='row' classes={['gap-12']}>
              <Button
                text='Review'
                variant='primary-btn'
                isDisabled={!canReview}
                handleClick={() => setConfirming(true)}
                dataTestId='manual-sale-review'
              />
              <Button text='Cancel' variant='secondary-btn' handleClick={onClose} />
            </Flex>
          </Flex>
        )}
      </div>
    </InputModal>
  )
}

export { ManualShareSaleModal }
